import React from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, CalendarClock, CreditCard, Key, Check } from 'lucide-react';

/**
 * Thin, sticky, responsive stepper for the /plan booking flow.
 * Steps are clickable when navigable (previous / current step).
 * Props:
 *  - activeStep: number (1..4)
 *  - onNavigate: (stepNumber) => void
 *  - canGoTo: (stepNumber) => boolean  (optional gate, defaults to prev-only navigation)
 */
export default function PlanStepper({ activeStep, onNavigate, canGoTo }) {
  const { t } = useTranslation();
  const steps = [
    { n: 1, icon: MapPin,        title: t('landing.step1Title', 'Choisissez un espace') },
    { n: 2, icon: CalendarClock, title: t('landing.step2Title', "Choisissez un horaire") },
    { n: 3, icon: CreditCard,    title: t('landing.step3Title', 'Vérifiez et payer') },
    { n: 4, icon: Key,           title: t('landing.step4Title', 'Accédez') },
  ];
  const gate = canGoTo || ((n) => n <= activeStep);

  return (
    <div
      className="sticky top-14 sm:top-16 z-30 bg-white/95 backdrop-blur-sm border-b border-[#E2E2DF] shadow-sm"
      data-testid="plan-stepper-sticky"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-1 sm:gap-2 h-11 sm:h-12">
          {steps.map((s, idx) => {
            const done = s.n < activeStep;
            const current = s.n === activeStep;
            const clickable = gate(s.n) && !current;
            const StepIcon = s.icon;
            return (
              <React.Fragment key={s.n}>
                <button
                  type="button"
                  disabled={!clickable}
                  onClick={() => clickable && onNavigate?.(s.n)}
                  data-testid={`plan-step-${s.n}`}
                  className={`flex items-center gap-1.5 sm:gap-2 min-w-0 rounded-md px-1 py-1 transition-colors ${
                    clickable ? 'hover:bg-[#F0EFEA] cursor-pointer' : ''
                  } disabled:cursor-default`}
                >
                  <div
                    className={`flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-[11px] sm:text-xs font-bold transition-colors ${
                      current
                        ? 'bg-[#C77D63] text-white ring-2 ring-[#C77D63]/25'
                        : done
                        ? 'bg-[#1A3C34] text-white'
                        : 'bg-[#F0EFEA] text-[#B7B7B0]'
                    }`}
                  >
                    {done ? <Check className="w-3 h-3" /> : s.n}
                  </div>
                  <StepIcon
                    className={`hidden sm:block flex-shrink-0 w-3.5 h-3.5 ${
                      current ? 'text-[#C77D63]' : done ? 'text-[#1A3C34]' : 'text-[#B7B7B0]'
                    }`}
                  />
                  <span
                    className={`hidden md:block text-xs lg:text-sm font-semibold whitespace-nowrap truncate ${
                      current ? 'text-[#1A3C34]' : 'text-[#737373]'
                    }`}
                  >
                    {s.title}
                  </span>
                </button>
                {idx < steps.length - 1 && (
                  <div className={`flex-1 h-px min-w-[8px] ${done ? 'bg-[#1A3C34]/40' : 'bg-[#E2E2DF]'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
